import type { AxiosError, AxiosResponse } from 'axios';
import { all, call, debounce, put, race, take, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { DEVICES_LIMIT_REACHED } from 'constants/ErrorConstants';
import { DEBOUNCE_TIME } from 'constants/Global';
import { putAndWait } from 'modules/app/sagas';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import type { SimpleRequestConfig } from 'modules/app/types';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as deviceActions from 'modules/devices/actions/core';
import * as types from 'modules/devices/actionTypes';
import type * as DeviceType from 'modules/devices/types';
import { isApiFailure } from 'modules/global/generic.guards';
import type { ApiError, PageContent, PutAndWaitResult } from 'modules/global/generic.types';

// To avoid making unnecessary API calls for short names,
// we wait for the user to type at least 3 characters before triggering the check.
const SKIP_THRESHOLD = 3;

const getRequestErrorType = (error: unknown) =>
  (error as AxiosError<{ errorType?: string }>)?.response?.data?.errorType ?? String(error);

/**
 * CRUD mutations handled declaratively: request + success/failure dispatch + notifications.
 * The dashboard list is refreshed on CREATE/DELETE success by a watcher in `dashboard.saga`.
 */
const simpleRequests: Record<string, SimpleRequestConfig> = {
  [types.CREATE_DEVICE_REQUEST]: {
    callbacks: {
      failure: (error: ApiError) => deviceActions.createDeviceFailure(getRequestErrorType(error)),
    },
    notifications: {
      failure: (failureAction: DeviceType.CreateDeviceFailureAction) => {
        const isDeviceLimitReached = failureAction.payload === DEVICES_LIMIT_REACHED;
        return {
          id: `page.device.errors.${isDeviceLimitReached ? 'deviceLimitReach' : 'create'}`,
          defaultMessage: isDeviceLimitReached
            ? 'You have reached the maximum number of devices you can add with your current subscription. Either remove existing devices or contact us to upgrade your subscription.'
            : "Can't create the device",
        };
      },
    },
  },
  [types.DELETE_DEVICE_REQUEST]: {
    notifications: {
      success: { id: 'page.device.successfulDelete', defaultMessage: 'Device deleted' },
      failure: { id: 'page.device.errors.delete', defaultMessage: "Can't delete the device" },
    },
  },
  [types.PATCH_DEVICE]: {
    callbacks: {
      failure: (error, meta) => deviceActions.patchDeviceFailure(getRequestErrorType(error), meta),
    },
    notifications: {
      success: { id: 'page.device.update.success', defaultMessage: 'Device updated' },
      failure: { id: 'page.device.errors.update', defaultMessage: "Can't update the device" },
    },
  },
  // GET / send-QR: success auto-generated, no user-facing notification → suppress the default failure toast.
  [types.FETCH_DEVICES_REQUEST]: { notifications: { failure: () => undefined } },
  [types.FETCH_DEVICE]: { notifications: { failure: () => undefined } },
  [types.SEND_BY_EMAIL]: { notifications: { failure: () => undefined } },
};

function* checkDeviceNameWorker({ payload, meta }: DeviceType.DeviceNameCheckAction) {
  const { request } = payload!;
  const name = request.params?.name;
  const skip = !name || name.length < SKIP_THRESHOLD;
  let callbackAction;

  if (!skip) {
    try {
      const { response }: { response?: AxiosResponse } = yield race({
        abort: take(types.DEVICE_NAME_CHECK_ABORT),
        response: call(restClient.request, request),
      });

      if (response?.status) {
        callbackAction =
          response.status !== 204
            ? deviceActions.deviceNameCheckSuccess(meta)
            : deviceActions.deviceNameCheckFailure('page.device.validation.customValidate.message', meta);
      }
    } catch (error) {
      callbackAction =
        (error as AxiosError).response?.status === 404
          ? deviceActions.deviceNameCheckSuccess(meta)
          : deviceActions.deviceNameCheckFailure('page.device.validation.customValidate.message', meta);
    }

    if (callbackAction) {
      yield put(callbackAction);
    }
  }
}

function* onSelectDevice({ payload: deviceId, meta }: DeviceType.SelectDeviceAction) {
  const response: PutAndWaitResult<DeviceType.Device> = yield putAndWait(deviceActions.fetchDevice(deviceId));

  if (!isApiFailure(response)) {
    yield put(deviceActions.setUpdateModalVisibility(true, meta));
  }
}

function* fetchAppVersionWorker({ payload, meta }: DeviceType.FetchDevicesAppVersionAction) {
  try {
    const data: PageContent<DeviceType.DeviceAppVersionBucket> = yield* fetchPage<DeviceType.DeviceAppVersionBucket>(payload!.request);
    yield put(deviceActions.fetchDevicesAppVersionSuccess(data, meta));
  } catch (error) {
    yield put(deviceActions.fetchDevicesAppVersionFailure(error as ApiError, meta));
  }
}

function* fetchOSVersionWorker({ payload, meta }: DeviceType.FetchDevicesOSVersionAction) {
  try {
    const data: PageContent<DeviceType.DeviceOSVersionBucket> = yield* fetchPage<DeviceType.DeviceOSVersionBucket>(payload!.request);
    yield put(deviceActions.fetchDevicesOSVersionSuccess(data, meta));
  } catch (error) {
    yield put(deviceActions.fetchDevicesOSVersionFailure(error as ApiError, meta));
  }
}

export default function* coreSaga() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield takeLatest(types.SELECT_DEVICE, onSelectDevice);
  yield takeLatest(types.FETCH_DEVICES_APP_REQUEST, fetchAppVersionWorker);
  yield takeLatest(types.FETCH_DEVICES_OS_VERSION_REQUEST, fetchOSVersionWorker);

  yield debounce(DEBOUNCE_TIME, types.DEVICE_NAME_CHECK_REQUEST, checkDeviceNameWorker);
}
